
import { DateRange } from "react-day-picker";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { formatSafeDate } from "@/lib/dateUtils";
import { SUPABASE_PROJECT_URL } from '@/config/supabase';
import { debugLog, debugError } from '@/utils/debugConfig';

export interface RevenueDataPoint {
  name: string;
  Revenue: number;
  date: string;
}

export interface CoursePerformance {
  id: string;
  name: string;
  revenue: number;
  sales: number;
  change: number;
}

export interface Transaction {
  id: string;
  date: string;
  course: string;
  student: string;
  amount: number;
  status: "completed" | "pending";
  stripe_invoice_url?: string;
}

export interface BalanceSummary {
  available: number;
  pending: number;
  nextPayout: {
    amount: number;
    date: string;
  };
}

export interface PayoutHistory {
  id: string;
  date: string;
  amount: number;
  status: "paid" | "processing";
}

export interface SalesStats {
  totalRevenue: number;
  periodRevenue: number;
  coursesSold: number;
  avgOrderValue: number;
  periodChange: number;
}

/**
 * ConnectivityStatus tracks the health state of edge function connectivity
 */
export interface ConnectivityStatus {
  directFetchWorking: boolean;
  invokeFunctionWorking: boolean;
  pingDetails?: any;
  error?: string;
  lastChecked: Date;
}

// Singleton to track network connectivity status across the app
let lastConnectivityCheck: ConnectivityStatus | null = null;

const getCurrentUserId = async (): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");
  return user.id;
}

const getDateParams = (dateRange: DateRange | undefined) => {
  if (!dateRange?.from) {
    return { startDate: null, endDate: null };
  }
  
  const startDate = dateRange.from.toISOString().split('T')[0];
  const endDate = dateRange.to ? dateRange.to.toISOString().split('T')[0] : startDate;
  
  return { startDate, endDate };
}

/**
 * Tests edge function connectivity and returns detailed results
 */
export async function testEdgeFunctionConnectivity(): Promise<ConnectivityStatus> {
  try {
    debugLog('NETWORK', "Testing edge function connectivity");
    
    const pingUrl = `${SUPABASE_PROJECT_URL}/functions/v1/ping`;
    debugLog('NETWORK', "Ping URL:", pingUrl);
    
    const results: ConnectivityStatus = {
      directFetchWorking: false,
      invokeFunctionWorking: false,
      pingDetails: undefined,
      error: undefined,
      lastChecked: new Date()
    };
    
    try {
      debugLog('NETWORK', "Test 1: Direct fetch using URL:", pingUrl);
      const startTime = performance.now();
      const directResponse = await fetch(pingUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client: "browser", timestamp: new Date().toISOString() })
      });
      
      if (directResponse.ok) {
        const data = await directResponse.json();
        const endTime = performance.now();
        debugLog('NETWORK', `Direct fetch succeeded in ${(endTime - startTime).toFixed(0)}ms:`, data);
        results.directFetchWorking = true;
        results.pingDetails = data;
      } else {
        const errorText = await directResponse.text();
        debugError('NETWORK', `Direct fetch failed with status: ${directResponse.status}`, errorText);
      }
    } catch (fetchError: any) {
      debugError('NETWORK', "Direct fetch error:", fetchError);
      results.error = `Direct fetch failed: ${fetchError.message}`;
    }
    
    try {
      debugLog('NETWORK', "Test 2: Using supabase.functions.invoke");
      const startTime = performance.now();
      const invokeResult = await supabase.functions.invoke('ping', {
        method: 'POST',
        body: { test: true, client: "invoke", timestamp: new Date().toISOString() }
      });
      
      if (invokeResult.error) {
        debugError('NETWORK', "Invoke failed:", invokeResult.error);
      } else {
        const endTime = performance.now();
        debugLog('NETWORK', `Invoke succeeded in ${(endTime - startTime).toFixed(0)}ms:`, invokeResult.data);
        results.invokeFunctionWorking = true;
        if (!results.pingDetails) {
          results.pingDetails = invokeResult.data;
        }
      }
    } catch (invokeError: any) {
      debugError('NETWORK', "Invoke error:", invokeError);
      results.error = `${results.error || ''}\nInvoke failed: ${invokeError.message}`;
    }
    
    // Update the singleton reference
    lastConnectivityCheck = results;
    return results;
  } catch (error: any) {
    debugError('NETWORK', "Error in connectivity test:", error);
    const results: ConnectivityStatus = {
      directFetchWorking: false,
      invokeFunctionWorking: false,
      error: error.message,
      lastChecked: new Date()
    };
    lastConnectivityCheck = results;
    return results;
  }
}

/**
 * Performs a comprehensive health check on edge functions
 */
export async function checkEdgeFunctionHealth(): Promise<boolean> {
  try {
    debugLog('SALES', "Checking edge function health");
    
    // Use cached connectivity test result if it's recent (last 30 seconds)
    const shouldUseCache = lastConnectivityCheck && 
                           (new Date().getTime() - lastConnectivityCheck.lastChecked.getTime() < 30000);
                           
    const pingTest = shouldUseCache ? 
                     lastConnectivityCheck : 
                     await testEdgeFunctionConnectivity();
    
    if (pingTest.directFetchWorking || pingTest.invokeFunctionWorking) {
      debugLog('SALES', "🟢 Basic connectivity test passed. Testing query-transactions function");
      
      if (pingTest.directFetchWorking) {
        try {
          const functionUrl = `${SUPABASE_PROJECT_URL}/functions/v1/query-transactions`;
          debugLog('SALES', "Attempting direct fetch health check to", functionUrl);
          
          const response = await fetch(functionUrl, {
            method: 'GET',  // Health check uses GET
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            debugLog('SALES', "Direct fetch health check successful:", data);
            return true;
          } else {
            debugError('SALES', `Direct fetch health check failed with status: ${response.status}`);
          }
        } catch (fetchError) {
          debugError('SALES', "Direct fetch health check failed:", fetchError);
        }
      }
      
      if (pingTest.invokeFunctionWorking) {
        try {
          debugLog('SALES', "Attempting health check via supabase.functions.invoke");
          const result = await supabase.functions.invoke('query-transactions', {
            method: 'GET'  // Health check uses GET
          });
          
          if (result.error) {
            debugError('SALES', "Invoke health check error:", result.error);
          } else {
            debugLog('SALES', "Invoke health check successful:", result.data);
            return true;
          }
        } catch (invokeError) {
          debugError('SALES', "Invoke health check failed:", invokeError);
        }
      }
      
      debugError('SALES', "All health check methods failed despite ping working");
      return false;
    } else {
      debugError('SALES', "🔴 Basic connectivity tests failed. Network access may be restricted.");
      debugError('SALES', "Error details:", pingTest.error);
      return false;
    }
  } catch (error: any) {
    debugError('SALES', "Edge function health check error:", error);
    return false;
  }
}

export async function fetchSalesStats(dateRange: DateRange | undefined): Promise<SalesStats> {
  try {
    const userId = await getCurrentUserId();
    const periodDays = dateRange?.from && dateRange?.to ? 
      Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 3600 * 24)) : 
      30;
    
    const result = await supabase
      .rpc('get_creator_sales_stats', { 
        p_creator_id: userId,
        period_days: periodDays 
      });

    if (result.error) throw result.error;
    debugLog('SALES', 'Sales stats fetched:', result.data);
    
    return result.data as unknown as SalesStats;
  } catch (error) {
    debugError('SALES', "Error fetching sales stats:", error);
    toast({
      title: "Error loading sales statistics",
      description: "Please try again later",
      variant: "destructive"
    });
    
    throw new Error("Failed to fetch sales statistics");
  }
}

export async function fetchRevenueData(dateRange: DateRange | undefined, interval: "daily" | "weekly" | "monthly"): Promise<RevenueDataPoint[]> {
  try {
    const userId = await getCurrentUserId();
    const { startDate, endDate } = getDateParams(dateRange);
    
    const result = await supabase
      .rpc('get_creator_revenue_data', {
        p_creator_id: userId,
        p_start_date: startDate,
        p_end_date: endDate,
        p_interval: interval
      });

    if (result.error) throw result.error;
    debugLog('SALES', 'Revenue data fetched:', result.data);
    
    return result.data as unknown as RevenueDataPoint[];
  } catch (error) {
    debugError('SALES', "Error fetching revenue data:", error);
    toast({
      title: "Error loading revenue data",
      description: "Please try again later",
      variant: "destructive"
    });
    
    throw new Error("Failed to fetch revenue data");
  }
}

export async function fetchCoursePerformance(dateRange: DateRange | undefined): Promise<CoursePerformance[]> {
  try {
    const userId = await getCurrentUserId();
    const { startDate, endDate } = getDateParams(dateRange);
    
    debugLog('SALES', `Fetching course performance for date range: ${startDate} to ${endDate}`);
    
    const result = await supabase
      .rpc('get_course_performance', {
        p_creator_id: userId,
        p_start_date: startDate,
        p_end_date: endDate
      });

    if (result.error) {
      debugError('SALES', "Course performance RPC error:", result.error);
      throw result.error;
    }
    
    if (!result.data || !Array.isArray(result.data)) {
      debugError('SALES', "Course performance returned non-array or empty result:", result.data);
      return [];
    }
    
    const typedData: CoursePerformance[] = result.data.map((item: any) => ({
      id: item.id || '',
      name: item.name || '',
      revenue: typeof item.revenue === 'number' ? item.revenue : 0,
      sales: typeof item.sales === 'number' ? item.sales : 0,
      change: typeof item.change === 'number' ? item.change : 0
    }));
    
    debugLog('SALES', `Course performance data: ${typedData.length} courses fetched`);
    return typedData;
  } catch (error) {
    debugError('SALES', "Error fetching course performance:", error);
    toast({
      title: "Error loading course performance",
      description: "Please try again later",
      variant: "destructive"
    });
    
    throw new Error("Failed to fetch course performance");
  }
}

/**
 * Fetches transaction data from edge function with robust fallback strategy
 */
export async function fetchTransactions(dateRange: DateRange | undefined): Promise<Transaction[]> {
  try {
    const userId = await getCurrentUserId();
    const { startDate, endDate } = getDateParams(dateRange);
    
    debugLog('TRANSACTIONS', `Fetching transactions for creator ${userId} with date range:`, { startDate, endDate });
    
    // Get current user session for authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }
    
    const connectivityTest = lastConnectivityCheck && 
      (new Date().getTime() - lastConnectivityCheck.lastChecked.getTime() < 60000) ?
      lastConnectivityCheck : await testEdgeFunctionConnectivity();
    
    const requestBody = { 
      creator_id: userId, 
      start_date: startDate, 
      end_date: endDate 
    };
    
    debugLog('TRANSACTIONS', "Transaction request payload:", requestBody);
    
    // Try direct fetch first if it's been working
    if (connectivityTest.directFetchWorking) {
      try {
        const functionUrl = `${SUPABASE_PROJECT_URL}/functions/v1/query-transactions`;
        debugLog('TRANSACTIONS', "Using direct fetch to edge function:", functionUrl);
        
        // Add proper authentication headers
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhiZ21mcXRzZGl6amVkb2FuaWVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ4MjY0MTgsImV4cCI6MjA2MDQwMjQxOH0.S6w7v1GkO9e7TwQZT4jaNcwWveofgT5OTWqYmlEBb7Q',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(requestBody)
        });
        
        debugLog('TRANSACTIONS', `Direct fetch POST request sent with auth token: ${session.access_token.substring(0, 15)}...`);
        
        if (!response.ok) {
          const errorText = await response.text();
          debugError('TRANSACTIONS', `Direct fetch failed with status: ${response.status}`, errorText);
          throw new Error(`Failed to fetch transactions: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        debugLog('TRANSACTIONS', "Transaction fetch successful, records:", data?.length || 0);
        
        if (!Array.isArray(data)) {
          debugError('TRANSACTIONS', "Response is not an array:", data);
          if (data.error) throw new Error(data.error);
          return [];
        }
        
        return data.map((tx: any) => ({
          id: tx.id || `temp-${Math.random().toString(36).substring(7)}`,
          date: tx.date || new Date().toISOString(),
          course: tx.course || 'Unknown Course',
          student: tx.student || 'Unknown Student',
          amount: typeof tx.amount === 'number' ? tx.amount : parseFloat(tx.amount) || 0,
          status: tx.status as "completed" | "pending",
          stripe_invoice_url: tx.stripe_invoice_url || undefined
        }));
      } catch (fetchError) {
        debugError('TRANSACTIONS', "Direct fetch failed:", fetchError);
        debugLog('TRANSACTIONS', "Falling back to invoke method...");
      }
    }
    
    // Fallback to invoke method
    debugLog('TRANSACTIONS', "Using supabase.functions.invoke method");
    const { data, error } = await supabase.functions.invoke('query-transactions', {
      body: requestBody
    });
    
    if (error) {
      debugError('TRANSACTIONS', "Transaction function error details:", {
        message: error.message,
        name: error.name,
        context: error?.context,
        stack: error?.stack
      });
      throw error;
    }
    
    if (!Array.isArray(data)) {
      debugError('TRANSACTIONS', "Invalid response format:", data);
      return [];
    }
    
    return data.map((tx: any) => ({
      id: tx.id || `temp-${Math.random().toString(36).substring(7)}`,
      date: tx.date || new Date().toISOString(),
      course: tx.course || 'Unknown Course',
      student: tx.student || 'Unknown Student',
      amount: typeof tx.amount === 'number' ? tx.amount : parseFloat(tx.amount) || 0,
      status: tx.status as "completed" | "pending",
      stripe_invoice_url: tx.stripe_invoice_url || undefined
    }));
    
  } catch (error: any) {
    debugError('TRANSACTIONS', "Error fetching transactions:", error);
    toast({
      title: "Error loading transactions",
      description: error.message || "Could not retrieve transaction data. Please try again later.",
      variant: "destructive"
    });
    
    return [];
  }
}

export function exportTransactionsCSV(transactions: Transaction[]): void {
  if (!transactions.length) {
    toast({
      title: "No transactions to export",
      description: "There are no transactions in the selected period.",
      variant: "destructive"
    });
    return;
  }

  const headers = ["Date", "Course", "Student", "Amount", "Status"];
  const csvRows = [
    headers.join(","),
    ...transactions.map(t => {
      return [
        formatSafeDate(t.date, "yyyy-MM-dd"),
        `"${t.course.replace(/"/g, '""')}"`,
        `"${t.student.replace(/"/g, '""')}"`,
        t.amount,
        t.status
      ].join(",");
    })
  ];
  
  const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
  
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `transactions_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast({
    title: "Export successful",
    description: "Transaction data has been exported to CSV.",
  });
}
